import { makeAutoObservable } from "mobx";

import { Vec2 } from "utils/math/vec2";
import { Webgl2Component } from "../webgl2-component";
import {
  DisplayStyleType,
  PointerEventsStyleType,
  StyleSupportedUnits,
  StyleValueParsed,
} from "./style.types";

export class Style {
  public top = "";
  public bottom = "";
  public left = "";
  public right = "";

  public translateY = "";
  public translateX = "";
  public scaleX = 1;
  public scaleY = 1;

  public display: DisplayStyleType = "initial";
  public pointerEvents: PointerEventsStyleType = "initial";

  constructor() {
    makeAutoObservable(this);
  }

  public getTBLRTranslationVec2(component: Webgl2Component<any, any>): Vec2 {
    const topOrBottom = this.top || this.bottom;
    const topOrBottomParsed = this._parseStyleTextValue(topOrBottom);
    const parentHeight = component?.parent?.transform.size.height || 0;
    const topOrBottomValue = topOrBottomParsed.reduce<number>((acc, current) => {
      const parsedUnit = this._parseUnitValue(current.unit, current.value, parentHeight);
      return acc + parsedUnit;
    }, 0);

    const valueY = this.top
      ? topOrBottomValue
      : this.bottom
      ? -topOrBottomValue + parentHeight - component.transform.size.height
      : 0;

    const leftOrRight = this.left || this.right;
    const leftOrRightParsed = this._parseStyleTextValue(leftOrRight);
    const parentWidth = component?.parent?.transform.size.width || 0;
    const leftOrRightValue = leftOrRightParsed.reduce<number>((acc, current) => {
      const parsedUnit = this._parseUnitValue(current.unit, current.value, parentWidth);
      return acc + parsedUnit;
    }, 0);
    const valueX = this.left
      ? leftOrRightValue
      : this.right
      ? -leftOrRightValue + parentWidth - component.transform.size.width
      : 0;

    return new Vec2(valueX, valueY);
  }

  public getTranslateTranslationVec2(component: Webgl2Component<any, any>): Vec2 {
    const translateYParsed = this._parseStyleTextValue(this.translateY);
    const translateYValue = translateYParsed.reduce<number>((acc, current) => {
      const parsedUnit = this._parseUnitValue(
        current.unit,
        current.value,
        component.transform.size.height,
      );

      return acc + parsedUnit;
    }, 0);

    const translateXParsed = this._parseStyleTextValue(this.translateX);
    const translateXValue = translateXParsed.reduce<number>((acc, current) => {
      const parsedUnit = this._parseUnitValue(
        current.unit,
        current.value,
        component.transform.size.width,
      );

      return acc + parsedUnit;
    }, 0);

    return new Vec2(translateXValue, translateYValue);
  }

  public getScaleVec2() {
    return new Vec2(this.scaleX, this.scaleY);
  }

  private _parseStyleTextValue(string: string): StyleValueParsed[] {
    const result: StyleValueParsed[] = [];

    string.split("&").forEach((string) => {
      let unit = "";
      let value = "";

      for (let i = string.length - 1; i >= 0; i--) {
        const char = string[i];
        const parsedChar = Number(char);

        if (!isNaN(parsedChar)) {
          unit = string.slice(i + 1);
          value = string.slice(0, i + 1);
          break;
        }
      }

      result.push({ unit: unit as StyleSupportedUnits, value: Number(value) });
    });

    return result;
  }

  private _parseUnitValue(unit: StyleSupportedUnits, value: number, parentValue: number): number {
    switch (unit) {
      case "%":
        return (parentValue * value) / 100;
      default:
        return value;
    }
  }
}
